import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2, Sparkles } from "lucide-react";
import { BRAND_SLOGAN, LogoEmblem } from "./Logo";

export default function Hero() {
  return (
    <section
      data-testid="home-hero"
      className="relative bg-navy text-white overflow-hidden"
    >
      {/* Backdrop image */}
      <div className="absolute inset-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/a40b2e9c-8f18-467c-802c-d2ada63af7ec/images/3b316e15136d2092b745c600aef1af8b5f67c579efde4aaeaeb1f43095fbe4c7.png"
          alt=""
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="absolute inset-0 hero-radial" />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 pt-36 pb-24 lg:pt-44 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-3 px-4 py-2 border border-white/15 bg-white/[0.03] text-xs uppercase tracking-[0.22em] text-gold">
              <Sparkles size={12} /> UAE · Global Education Leadership
            </div>
            <h1
              data-testid="hero-headline"
              className="mt-7 font-display font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight"
            >
              Building the Future of <br />
              <span className="text-gold">Global Education</span> &<br />
              Institutional Experiences.
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-8 max-w-2xl text-base sm:text-lg text-white/75 leading-relaxed"
            >
              Advanced digital ecosystems, global academic events, and
              international exposure programs for institutions worldwide.
            </p>

            <div
              data-testid="hero-slogan"
              className="mt-7 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-gold"
            >
              <span className="h-px w-10 bg-gold/60" />
              {BRAND_SLOGAN}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/school-os"
                data-testid="hero-cta-explore"
                className="group inline-flex items-center gap-2 bg-gold text-navy px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-gold-300 transition"
              >
                Explore Solutions
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
              <Link
                to="/partners"
                data-testid="hero-cta-partner"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-gold hover:text-gold px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition"
              >
                Partner With Us
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-consult"
                className="inline-flex items-center gap-2 text-white/80 hover:text-gold px-3 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition"
              >
                Request Consultation →
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="flex justify-end mb-6 lg:mb-8">
              <LogoEmblem size={120} variant="ghostDark" withRing={false} />
            </div>
            <div className="border border-white/10 bg-white/[0.03] backdrop-blur-sm p-7">
              <div className="flex items-center gap-3 text-gold">
                <Globe2 size={18} />
                <span className="text-xs uppercase tracking-[0.22em]">
                  Global Footprint
                </span>
              </div>
              <div className="mt-7 grid grid-cols-3 gap-4">
                {[
                  { k: "5+", v: "Countries Engaged" },
                  { k: "25+", v: "Institutions Served" },
                  { k: "End", v: "to End Execution" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="font-display text-3xl text-white">
                      {s.k}
                    </div>
                    <div className="mt-1 text-[11px] uppercase tracking-widest text-white/50">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-7 pt-6 border-t border-white/10 text-sm text-white/70 leading-relaxed">
                A UAE-based institutional partner. Built for governments,
                councils, schools, colleges, and universities worldwide.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* bottom marquee strip */}
      <div className="relative border-y border-white/10 bg-navy/60 backdrop-blur-sm">
        <div className="overflow-hidden">
          <div className="marquee-track flex gap-16 py-5 text-xs uppercase tracking-[0.32em] text-white/60 whitespace-nowrap">
            {Array.from({ length: 2 }).map((_, k) => (
              <React.Fragment key={k}>
                <span>School Operating System</span>
                <span className="text-gold">◆</span>
                <span>Event & MUN OS</span>
                <span className="text-gold">◆</span>
                <span>Educational Tourism</span>
                <span className="text-gold">◆</span>
                <span>Institutional Travel</span>
                <span className="text-gold">◆</span>
                <span>Destination Management</span>
                <span className="text-gold">◆</span>
                <span>Global Partnerships</span>
                <span className="text-gold">◆</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
